import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, Button, PageHeader, Spinner, EmptyState, Badge } from '../components/ui';

interface Lesson {
  id: string;
  title: string;
  type: 'video' | 'text' | 'quiz' | 'assignment';
  videoUrl?: string;
  content?: string;
}

interface Section {
  id: string;
  title: string;
  lessons: Lesson[];
}

interface Course {
  id: string;
  title: string;
  description?: string;
  slug: string;
  sections: Section[];
}

interface Progress {
  lessonId: string;
  completed: boolean;
  completedAt?: string;
}

export function LearnPage() {
  const { courseSlug } = useParams<{ courseSlug: string }>();
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);

  const courseQ = useQuery({
    queryKey: ['public-course', courseSlug],
    queryFn: () => api.get<Course>(`/public/courses/${courseSlug}`),
  });

  const progressQ = useQuery({
    queryKey: ['my-progress', courseSlug],
    queryFn: () => api.get<{ data: Progress[] }>(`/me/progress/${courseSlug}`).catch(() => ({ data: [] })),
    enabled: !!courseSlug,
  });

  const completeMut = useMutation({
    mutationFn: (lessonId: string) =>
      api.post(`/me/progress/${courseSlug}/lessons/${lessonId}/complete`),
    onSuccess: () => progressQ.refetch(),
  });

  const allLessons = (courseQ.data?.sections ?? []).flatMap((s) => s.lessons);

  useEffect(() => {
    if (!activeLessonId && allLessons.length > 0) {
      setActiveLessonId(allLessons[0].id);
    }
  }, [allLessons, activeLessonId]);

  const activeLesson = allLessons.find((l) => l.id === activeLessonId);
  const completedSet = new Set((progressQ.data?.data ?? []).filter((p) => p.completed).map((p) => p.lessonId));
  const progressPct = allLessons.length > 0 ? Math.round((completedSet.size / allLessons.length) * 100) : 0;

  if (courseQ.isLoading) return <Spinner />;
  if (!courseQ.data) {
    return (
      <Card>
        <EmptyState title="Course not found" description="Check the link and try again." />
      </Card>
    );
  }

  const course = courseQ.data;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 'var(--space-4)', minHeight: '100vh', padding: 'var(--space-6)', background: 'var(--color-surface-secondary)' }}>
      <aside>
        <Card>
          <h2 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-lg)', fontWeight: 700 }}>{course.title}</h2>
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ height: 6, background: 'var(--color-surface-tertiary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--color-feedback-success)' }} />
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>{progressPct}% complete</div>
          </div>
          {course.sections.map((section) => (
            <div key={section.id} style={{ marginBottom: 'var(--space-3)' }}>
              <h4 style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>{section.title}</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {section.lessons.map((l) => (
                  <li
                    key={l.id}
                    onClick={() => setActiveLessonId(l.id)}
                    style={{
                      padding: 'var(--space-1) var(--space-2)',
                      cursor: 'pointer',
                      fontSize: 'var(--text-sm)',
                      borderRadius: 'var(--radius-md)',
                      background: l.id === activeLessonId ? 'var(--color-brand-primary-light)' : 'transparent',
                      color: l.id === activeLessonId ? 'var(--color-brand-primary)' : 'inherit',
                      display: 'flex',
                      gap: 'var(--space-2)',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ width: 16 }}>{completedSet.has(l.id) ? '✓' : '○'}</span>
                    <span style={{ flex: 1 }}>{l.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Card>
      </aside>

      <main>
        {activeLesson ? (
          <Card>
            <PageHeader title={activeLesson.title} subtitle={activeLesson.type} />
            {activeLesson.type === 'video' && activeLesson.videoUrl && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <video src={activeLesson.videoUrl} controls style={{ width: '100%', borderRadius: 'var(--radius-lg)' }} />
              </div>
            )}
            {activeLesson.content && (
              <div style={{ fontSize: 'var(--text-base)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{activeLesson.content}</div>
            )}
            <div style={{ marginTop: 'var(--space-6)', display: 'flex', justifyContent: 'space-between' }}>
              <Badge color={completedSet.has(activeLesson.id) ? 'green' : 'gray'}>
                {completedSet.has(activeLesson.id) ? 'Completed' : 'In progress'}
              </Badge>
              <Button onClick={() => completeMut.mutate(activeLesson.id)} disabled={completeMut.isPending || completedSet.has(activeLesson.id)}>
                {completedSet.has(activeLesson.id) ? 'Done' : 'Mark complete'}
              </Button>
            </div>
          </Card>
        ) : (
          <EmptyState title="Pick a lesson to begin" />
        )}
      </main>
    </div>
  );
}
